import React, { useEffect, useMemo, useState } from "react";
import Sidebar from "../../components/admin/sidebar/Sidebar";
import { ToastContainer } from "react-toastify";
import useDash from "../../assets/css/dash.module.css";
import { FontColor, FourGround } from "../../assets/js/theme";
import { useTranslation } from "react-i18next";
import Loader from "../../components/common/Loader";
import { ArrowClockwise } from "react-bootstrap-icons";
import Footer from "../../components/admin/footer/Footer";
import { useDispatch, useSelector } from "react-redux";
import CanvasJSReact from "@canvasjs/react-charts";
import {
  getCustomersBandwidthUsageChart,
  getHighlyBandwidthUsageCustomers,
} from "../../features/apiCalls";
import moment from "moment";
import Table from "../../components/table/Table";

const CanvasJSChart = CanvasJSReact.CanvasJSChart;

const CustomerTraffic = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  //--->  @Get dashboard middle monthly collection graph chart data
  const chartsData = useSelector((state) => state.chart.bandwidthUsageCharts);

  const customers = useSelector(
    (state) => state?.customer?.bandwidthUsageCustomers,
  );

  //---> Local states
  const [loading, setLoading] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [dps1, setDps1] = useState([]);
  const [dps2, setDps2] = useState([]);
  const [xVal, setXVal] = useState(0);
  const [filter, setFilter] = useState("month");

  const dataLength = new Date(
    new Date().getFullYear(),
    new Date().getMonth() + 1,
    0,
  ).getDate();

  const formatToMbps = (value) => {
    return (value / 1024 / 1024).toFixed(2);
  };

  useEffect(() => {
    if (filter) {
      getCustomersBandwidthUsageChart(dispatch, filter, setLoading);
      getHighlyBandwidthUsageCustomers(dispatch, setIsLoading);
    }
  }, [filter]);

  const reloadHandler = () => {
    getCustomersBandwidthUsageChart(dispatch, filter, setLoading);
    getHighlyBandwidthUsageCustomers(dispatch, setIsLoading);
  };

  useEffect(() => {
    if (chartsData?.chart?.length > 0) {
      const newDps1 = chartsData.chart?.map((chunk, index) => ({
        x: index + 1,
        y: Number(formatToMbps(chunk.rxByte)),
        tooltipUploadValue:
          formatToMbps(chunk?.rxByte) / 1024 >= 1
            ? `${(formatToMbps(chunk.rxByte) / 1024).toFixed(2)} GB`
            : `${formatToMbps(chunk.rxByte)} MB`,
        date: chunk?.hour
          ? moment(chunk?.hour).format("YY-MM-DD hh:mm A")
          : chunk?.day,
      }));

      const newDps2 = chartsData.chart?.map((chunk, index) => ({
        x: index + 1,
        y: Number(formatToMbps(chunk.txByte)),
        tooltipDownloadValue:
          formatToMbps(chunk?.txByte) / 1024 >= 1
            ? `${(formatToMbps(chunk.txByte) / 1024).toFixed(2)} GB`
            : `${formatToMbps(chunk.txByte)} MB`,
        date: chunk?.hour
          ? moment(chunk?.hour).format("YY-MM-DD hh:mm A")
          : chunk?.day,
      }));

      setDps1(newDps1.slice(-dataLength));
      setDps2(newDps2.slice(-dataLength));

      setXVal(newDps1.length);
    } else {
      setDps1([]);
      setDps2([]);
      setXVal(0);
    }
  }, [chartsData]);

  const options = useMemo(
    () => ({
      exportEnabled: true,
      animationEnabled: true,
      axisX: {
        interval: 1,
        labelAutoFit: false,
      },
      axisY: {
        title: "Data Consumed (GB)",
      },
      data: [
        {
          type: "column",
          showInLegend: true,
          legendText: `Upload: ${
            formatToMbps(chartsData?.upload) / 1024 >= 1
              ? (formatToMbps(chartsData?.upload) / 1024).toFixed(2)
              : formatToMbps(chartsData?.upload)
          } ${
            Number(formatToMbps(chartsData?.upload) / 1024) >= 1 ? "GB" : "MB"
          }`,
          dataPointWidth: 2,
          dataPoints: dps1,
          toolTipContent:
            "<div>" +
            "<p>{date}</p>" +
            "<p>Upload: {tooltipUploadValue}</p>" +
            "</div>",
        },
        {
          type: "column",
          showInLegend: true,
          legendText: `Download: ${
            formatToMbps(chartsData?.download) / 1024 >= 1
              ? (formatToMbps(chartsData?.download) / 1024).toFixed(2)
              : formatToMbps(chartsData?.download)
          } ${
            Number(formatToMbps(chartsData?.download) / 1024) >= 1 ? "GB" : "MB"
          }`,
          dataPointWidth: 2,
          dataPoints: dps2,
          toolTipContent:
            "<div>" +
            "<p>{date}</p>" +
            "<p>Download: {tooltipDownloadValue}</p>" +
            "</div>",
        },
      ],
    }),
    [xVal, dps1, dps2],
  );

  const columns = React.useMemo(
    () => [
      {
        width: "5%",
        Header: t("id"),
        accessor: "customer.customerId",
      },
      {
        width: "10%",
        Header: t("namePPPoE"),
        accessor: (data) =>
          `${data?.customer?.name} ${data?.customer?.pppoe?.name}`,
        Cell: ({ row: { original } }) => (
          <div>
            <p>{original?.customer?.name}</p>
            <p>{original?.customer?.pppoe?.name}</p>
          </div>
        ),
      },
      {
        width: "10%",
        Header: t("package"),
        accessor: "customer.pppoe.profile",
      },
      {
        width: "6%",
        Header: t("upload"),
        accessor: "rxByte",
        Cell: ({ cell: { value } }) => (
          <div>
            {value
              ? value / 1024 / 1024 <= 1024
                ? (value / 1024 / 1024).toFixed(2) + " MB"
                : (value / 1024 / 1024 / 1024).toFixed(2) + " GB"
              : ""}
          </div>
        ),
      },
      {
        width: "6%",
        Header: t("download"),
        accessor: "txByte",
        Cell: ({ cell: { value } }) => (
          <div>
            {value
              ? value / 1024 / 1024 <= 1024
                ? (value / 1024 / 1024).toFixed(2) + " MB"
                : (value / 1024 / 1024 / 1024).toFixed(2) + " GB"
              : ""}
          </div>
        ),
      },
      {
        width: "6%",
        Header: t("session"),
        accessor: "sessions",
      },
      {
        width: "12%",
        Header: t("lastLoggedOut"),
        accessor: "upTime",
        Cell: ({ cell: { value } }) => (
          <div>{moment(value).format("YYYY/MM/DD hh:mm A")}</div>
        ),
      },
    ],
    [t],
  );

  return (
    <>
      <Sidebar />
      <ToastContainer position="top-right" theme="colored" />
      <div className={useDash.dashboardWrapper}>
        <div className="container-fluied collector">
          <div className="container">
            <FontColor>
              <FourGround>
                <div className="collectorTitle d-flex justify-content-between px-4">
                  <div className="component_name">{t("customerTraffic")}</div>

                  <div className="d-flex justify-content-center align-items-center">
                    <div className="reloadBtn">
                      {isLoading ? (
                        <Loader />
                      ) : (
                        <ArrowClockwise
                          className="arrowClock"
                          title={t("refresh")}
                          onClick={reloadHandler}
                        />
                      )}
                    </div>
                  </div>
                </div>
              </FourGround>
              <FourGround>
                <div className="collectorWrapper mt-2 py-2">
                  <div>
                    {/* <div className="d-flex justify-content-center align-items-center p-1">
                      <div className="btn-group" role="group">
                        <button
                          className={`btn btn-outline-primary ${
                            filter === "day" ? "active" : ""
                          }`}
                          onClick={() => setFilter("day")}
                        >
                          {filter === "day" && isLoading ? <Loader /> : "Day"}
                        </button>

                        <button
                          className={`btn btn-outline-primary ${
                            filter === "month" ? "active" : ""
                          }`}
                          onClick={() => setFilter("month")}
                        >
                          {filter === "month" && isLoading ? (
                            <Loader />
                          ) : (
                            "Month"
                          )}
                        </button>
                      </div>
                    </div> */}

                    <CanvasJSChart options={options} />
                  </div>
                  <div>
                    <Table
                      isLoading={isLoading}
                      columns={columns}
                      data={customers}
                    />
                  </div>
                </div>
              </FourGround>

              <Footer />
            </FontColor>
          </div>
        </div>
      </div>
    </>
  );
};

export default CustomerTraffic;
